import {
  useState,
  useEffect,
  type FC,
} from 'react';
import {
  Box,
  Modal,
  Stack,
  Button,
  Typography,
  ModalDialog,
} from '@mui/joy';
import {
  gears3dGlow
} from '../assets';

const AUTO_REFRESH_COUNTDOWN = 10;

export type OutdatedAppVersionModalProps = {
  open?: boolean;
  onUpdate(): void;
};

export const OutdatedAppVersionModal: FC<OutdatedAppVersionModalProps> = ({ open, onUpdate }) => {
  const [countdown, setCountdown] = useState<number>(AUTO_REFRESH_COUNTDOWN);

  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(interval);
          onUpdate();
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open, onUpdate]);

  return (
    <Modal open={open || false}>
      <ModalDialog
        sx={({ breakpoints }) => ({
          gap: 2,
          padding: 3,
          width: 310,
          boxSizing: 'content-box',
          [breakpoints.down(389)]: {
            width: '100%',
            boxSizing: 'border-box',
          }
        })}>
        <Stack alignItems='center' spacing={1.5}>
          <Typography
            sx={({ palette, breakpoints }) => ({
              fontSize: 20,
              fontWeight: 600,
              fontStyle: 'normal',
              lineHeight: '30px',
              textAlign: 'center',
              color: palette.common.white,
              [breakpoints.down(389)]: {
                fontSize: 18,
              }
            })}>
            Update available
          </Typography>
          <Typography
            sx={({ palette, breakpoints }) => ({
              color: palette.common[150],
              fontSize: 14,
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: '120%',
              textAlign: 'center',
              [breakpoints.down(389)]: {
                fontSize: 13,
              }
            })}>
            Just a little refresh and you're good to go!<br />
            We've added some nice improvements for you.
          </Typography>
        </Stack>
        <Stack alignItems='center'>
          <Box
            component='img'
            src={gears3dGlow.default}
            sx={({ breakpoints }) => ({
              width: 145,
              height: 145,
              flexShrink: 0,
              aspectRatio: '1/1',
              transform: 'scaleX(-1)',
              [breakpoints.down(389)]: {
                width: 125,
                height: 125,
              }
            })}
          />
        </Stack>
        <Stack spacing={.5}>
          <Typography
            sx={({ palette, breakpoints }) => ({
              fontSize: 14,
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: '32px',
              textAlign: 'center',
              color: palette.common.white,
              [breakpoints.down(389)]: {
                fontSize: 12,
              }
            })}>
            Auto-refreshing in {countdown} seconds
          </Typography>
          <Button
            color='primary'
            variant='solid'
            onClick={onUpdate}>
            Refresh Now
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
